import { FC, useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CaretDownIcon, WalletIcon, UserIcon } from 'components/icons';
import ProfileDropdown from './profileDropdown';
import AnimatedNumber from '../utils/AnimatedNumber';
import { useAppSelector, useAppDispatch } from 'store/hooks';
import { fetchUserBalance } from 'store/authSlice';
import { fetchLevels } from 'store/levelSlice';
import { useIsPlayingRealGame } from 'hooks/useIsPlayingRealGame';
import { getUnlocalizedPath } from 'utils/localizedRouting';
import styles from './userMenu.module.scss';

interface UserMenuProps {
  balance?: number;
  currencyIcon?: React.ReactNode;
  onWalletClick?: () => void;
  onProfileClick?: () => void;
  onLogout?: () => void;
}

const UserMenu: FC<UserMenuProps> = ({ onWalletClick, onProfileClick, onLogout }) => {
  const { t } = useTranslation();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isWithdrawOpen, setIsWithdrawOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const withdrawRef = useRef<HTMLDivElement>(null);

  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const location = useLocation();

  const isPlayingRealGame = useIsPlayingRealGame();

  const prevPathnameRef = useRef(location.pathname);

  // Sync user balance when they leave the game play page
  useEffect(() => {
    const prevUnlocalized = getUnlocalizedPath(prevPathnameRef.current);
    const currentUnlocalized = getUnlocalizedPath(location.pathname);

    if (
      prevUnlocalized.startsWith('/game/casino/') &&
      !currentUnlocalized.startsWith('/game/casino/') &&
      user
    ) {
      dispatch(fetchUserBalance());
      dispatch(fetchLevels(process.env.REACT_APP_TENANT_ID || '1'));
    }

    prevPathnameRef.current = location.pathname;
  }, [location.pathname, user, dispatch]);

  const mainBalance = Number(user?.balances?.[0]?.balance || 0);
  const withdrawableBalance = Number(user?.balances?.[0]?.withdrawable || 0);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (menuRef.current && !menuRef.current.contains(target)) {
        setIsProfileOpen(false);
      }
      if (withdrawRef.current && !withdrawRef.current.contains(target)) {
        setIsWithdrawOpen(false);
      }
    };

    if (isProfileOpen || isWithdrawOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isProfileOpen, isWithdrawOpen]);

  const handleProfileClick = () => {
    setIsProfileOpen((prev) => !prev);
    setIsWithdrawOpen(false);
    if (onProfileClick) {
      onProfileClick();
    }
  };

  const handleWithdrawClick = () => {
    if (isPlayingRealGame) return;
    setIsWithdrawOpen((prev) => !prev);
    setIsProfileOpen(false);
  };

  return (
    <div className={styles.user_menu} ref={menuRef}>
      <div className={styles.balance_wrapper} ref={withdrawRef}>
        <div
          className={`${styles.balance_container} ${isPlayingRealGame ? styles.playing : ''}`}
          onClick={isPlayingRealGame ? undefined : handleWithdrawClick}
        >
          {isPlayingRealGame ? (
            <span className={styles.playing_text}>{t('userMenu.playing', 'Playing')}</span>
          ) : (
            <>
              <AnimatedNumber value={mainBalance} prefix="$" className={styles.balance_amount} />
              <CaretDownIcon
                className={`${styles.dropdown_arrow} ${isWithdrawOpen ? styles.open : ''}`}
              />
            </>
          )}
        </div>

        <div className={`${styles.withdraw_dropdown} ${isWithdrawOpen ? styles.show : ''}`}>
          <div className={styles.withdraw_content}>
            <span>{t('userMenu.availableToWithdraw', 'Available to withdraw')}</span>
            <AnimatedNumber
              value={withdrawableBalance}
              prefix="$"
              className={styles.withdraw_amount}
            />
          </div>
        </div>
      </div>

      <button
        className={styles.wallet_btn}
        onClick={onWalletClick}
        title={t('common.wallet', 'Wallet')}
      >
        <WalletIcon />
        <span className={styles.wallet_text}>{t('common.wallet', 'Wallet')}</span>
      </button>

      <button
        className={styles.profile_btn}
        onClick={handleProfileClick}
        aria-label="Open user profile menu"
      >
        <UserIcon />
        {!!user?.unreadNotificationCount && user.unreadNotificationCount > 0 && (
          <span className={styles.notificationBadge}>
            {user.unreadNotificationCount > 99 ? '99+' : user.unreadNotificationCount}
          </span>
        )}
      </button>

      <ProfileDropdown
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        onLogout={onLogout}
      />
    </div>
  );
};

export default UserMenu;
